import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Plus, X, Fingerprint, Zap, Check } from "lucide-react";

const INDUSTRIES = ["Financial Services", "Healthcare", "Retail", "Government", "Manufacturing", "Technology", "Education", "Energy", "Other"];
const INCIDENT_TYPES = ["Ransomware", "Phishing", "Data Exfiltration", "Insider Threat", "DDoS", "Malware Infection", "Account Takeover", "Supply Chain", "Unknown"];
const KINDS = [
  { k: "ip", label: "IP" },
  { k: "domain", label: "Domain" },
  { k: "url", label: "URL" },
  { k: "hash", label: "File Hash" },
];

const detectKind = (v) => {
  const t = v.trim();
  if (/^[a-fA-F0-9]{32}$|^[a-fA-F0-9]{40}$|^[a-fA-F0-9]{64}$/.test(t)) return "hash";
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(t)) return "ip";
  if (/^https?:\/\//i.test(t)) return "url";
  if (/\./.test(t)) return "domain";
  return "ip";
};

function StepPill({ n, label, current, done }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-sm border flex items-center justify-center font-mono text-xs ${
        current ? "border-[#00F0FF] text-[#00F0FF]" : done ? "border-[#34C759] text-[#34C759]" : "border-white/15 text-[#8E9196]"
      }`}>
        {done ? <Check className="w-4 h-4" /> : n}
      </div>
      <div className={`text-[11px] font-mono uppercase tracking-widest ${current ? "text-white" : "text-[#8E9196]"}`}>{label}</div>
    </div>
  );
}

export default function InvestigationNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState("");

  const [customer, setCustomer] = useState({
    customer_name: "",
    organization: "",
    industry: "Financial Services",
    contact_email: "",
    incident_type: "Ransomware",
    severity: "high",
    affected_assets: [],
  });
  const [assetInput, setAssetInput] = useState("");
  const [evidence, setEvidence] = useState([{ kind: "ip", value: "" }]);
  const [notes, setNotes] = useState("");

  const addAsset = () => {
    if (assetInput.trim()) {
      setCustomer({ ...customer, affected_assets: [...customer.affected_assets, assetInput.trim()] });
      setAssetInput("");
    }
  };

  const removeAsset = (i) => setCustomer({ ...customer, affected_assets: customer.affected_assets.filter((_, idx) => idx !== i) });

  const updateEvidence = (i, field, value) => {
    const copy = [...evidence];
    copy[i] = { ...copy[i], [field]: value };
    if (field === "value") copy[i].kind = detectKind(value);
    setEvidence(copy);
  };
  const addEvidence = () => setEvidence([...evidence, { kind: "ip", value: "" }]);
  const removeEvidence = (i) => setEvidence(evidence.filter((_, idx) => idx !== i));

  const validStep1 = customer.customer_name.trim() && customer.contact_email.trim();
  const validStep2 = evidence.some((e) => e.value.trim());

  const submit = async () => {
    setRunning(true);
    setRunStatus("Creating case…");
    try {
      const cleanEvidence = evidence.filter((e) => e.value.trim()).map((e) => ({ kind: e.kind, value: e.value.trim() }));
      const { data: created } = await api.post("/investigations", { customer, evidence: cleanEvidence, notes });
      setRunStatus(`Case ${created.case_number} created. Starting background investigation…`);
      await api.post(`/investigations/${created.id}/run`);
      toast.success(`Investigation ${created.case_number} started`);
      // Navigate immediately — detail page will stream progress via SSE
      nav(`/investigations/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Investigation failed");
      setRunning(false);
      setRunStatus("");
    }
  };

  const stepPill = (n, label) => <StepPill n={n} label={label} current={step === n} done={step > n} />;

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
        <Fingerprint className="w-3.5 h-3.5" /> new investigation
      </div>
      <h1 className="font-display font-black text-4xl mt-2 mb-8">Start a case</h1>

      <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
        {stepPill(1, "Customer intake")}
        <div className="flex-1 h-px bg-white/10" />
        {stepPill(2, "Evidence")}
        <div className="flex-1 h-px bg-white/10" />
        {stepPill(3, "Run investigation")}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="border border-white/10 bg-[#121212] rounded-sm p-6" data-testid="step-1">
          <div className="font-display text-2xl mb-1">Who are we investigating for?</div>
          <p className="text-sm text-[#8E9196] mb-5">This information is captured on the final report.</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Customer name *</label>
              <input required data-testid="intake-customer-name" value={customer.customer_name}
                onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })}
                placeholder="Acme Bank"
                className="w-full bg-transparent border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]" />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Organization</label>
              <input data-testid="intake-org" value={customer.organization}
                onChange={(e) => setCustomer({ ...customer, organization: e.target.value })}
                placeholder="Acme Corp"
                className="w-full bg-transparent border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Industry</label>
              <select data-testid="intake-industry" value={customer.industry}
                onChange={(e) => setCustomer({ ...customer, industry: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Contact email *</label>
              <input required type="email" data-testid="intake-contact"
                value={customer.contact_email}
                onChange={(e) => setCustomer({ ...customer, contact_email: e.target.value })}
                placeholder="ciso@customer.com"
                className="w-full bg-transparent border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Incident type</label>
              <select data-testid="intake-type" value={customer.incident_type}
                onChange={(e) => setCustomer({ ...customer, incident_type: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                {INCIDENT_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Severity</label>
              <div className="flex items-center border border-white/15 rounded-sm overflow-hidden">
                {["critical", "high", "medium", "low"].map((s) => (
                  <button type="button" key={s} onClick={() => setCustomer({ ...customer, severity: s })}
                    data-testid={`intake-sev-${s}`}
                    className={`flex-1 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      customer.severity === s ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"
                    }`}>{s}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Affected assets</label>
            <div className="flex gap-2">
              <input value={assetInput} onChange={(e) => setAssetInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAsset())}
                placeholder="srv-db-01, wks-hr-05, …"
                data-testid="intake-asset-input"
                className="flex-1 bg-transparent border border-white/15 px-3 py-2.5 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              <button onClick={addAsset} data-testid="intake-add-asset"
                className="border border-white/15 text-[#8E9196] hover:text-white px-3 rounded-sm">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {customer.affected_assets.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 border border-white/15 px-2 py-0.5 rounded-sm font-mono text-xs">
                  {a}
                  <button onClick={() => removeAsset(i)} className="text-[#8E9196] hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!validStep1}
              data-testid="step-1-next"
              className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50"
            >
              Next: Evidence <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="border border-white/10 bg-[#121212] rounded-sm p-6" data-testid="step-2">
          <div className="font-display text-2xl mb-1">What do we know?</div>
          <p className="text-sm text-[#8E9196] mb-5">Paste IPs, domains, URLs, or file hashes. Kind is auto-detected — you can override.</p>

          <div className="space-y-2">
            {evidence.map((ev, i) => (
              <div key={i} className="flex gap-2">
                <select value={ev.kind} onChange={(e) => updateEvidence(i, "kind", e.target.value)}
                  data-testid={`evidence-kind-${i}`}
                  className="bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-xs uppercase tracking-widest outline-none focus:border-[#00F0FF]">
                  {KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
                </select>
                <input value={ev.value} onChange={(e) => updateEvidence(i, "value", e.target.value)}
                  placeholder="185.220.101.45  ·  malicious.example.com  ·  https://phish.example.com  ·  44d886…"
                  data-testid={`evidence-value-${i}`}
                  className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
                <button onClick={() => removeEvidence(i)} disabled={evidence.length === 1}
                  className="border border-white/15 text-[#8E9196] hover:text-white px-3 rounded-sm disabled:opacity-30">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={addEvidence} data-testid="add-evidence"
              className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">
              <Plus className="w-3 h-3" /> Add another IOC
            </button>
          </div>

          <div className="mt-5">
            <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Analyst notes (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the investigator should know before we start…"
              data-testid="wizard-notes"
              className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF] resize-none" />
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} data-testid="step-2-back"
              className="inline-flex items-center gap-2 border border-white/15 text-[#8E9196] hover:text-white px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => setStep(3)} disabled={!validStep2}
              data-testid="step-2-next"
              className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
              Review <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="border border-white/10 bg-[#121212] rounded-sm p-6" data-testid="step-3">
          <div className="font-display text-2xl mb-1">Ready to investigate</div>
          <p className="text-sm text-[#8E9196] mb-5">Confirm details. ForensiQ will enrich IOCs across 6 intel sources and run ForensiQ AI for narrative + adversary attribution.</p>

          <div className="grid grid-cols-2 gap-4 mb-5 font-mono text-xs">
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#8E9196]">Customer</div>
              <div className="text-white mt-0.5">{customer.customer_name}</div>
              <div className="text-[#8E9196]">{customer.organization} · {customer.industry}</div>
              <div className="text-[#00F0FF]">{customer.contact_email}</div>
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#8E9196]">Incident</div>
              <div className="text-white mt-0.5">{customer.incident_type}</div>
              <div className="text-[#FF9F0A] uppercase">{customer.severity}</div>
              <div className="text-[#8E9196]">Assets: {customer.affected_assets.join(", ") || "—"}</div>
            </div>
          </div>

          <div className="border border-white/10 rounded-sm p-3 mb-5">
            <div className="text-[10px] uppercase tracking-widest text-[#8E9196] font-mono">Evidence ({evidence.filter((e) => e.value.trim()).length})</div>
            <div className="mt-1 space-y-1 font-mono text-xs">
              {evidence.filter((e) => e.value.trim()).map((e, i) => (
                <div key={i}>
                  <span className="text-[#00F0FF] uppercase mr-2">{e.kind}</span>
                  <span className="text-white">{e.value}</span>
                </div>
              ))}
            </div>
          </div>

          {running && (
            <div className="tracing-beam border border-[#00F0FF]/40 rounded-sm p-4 mb-5" data-testid="wizard-progress">
              <div className="flex items-center gap-2 text-[#00F0FF] font-mono text-sm">
                <Zap className="w-4 h-4 animate-pulse" /> {runStatus}
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} disabled={running}
              data-testid="step-3-back"
              className="inline-flex items-center gap-2 border border-white/15 text-[#8E9196] hover:text-white px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={submit} disabled={running}
              data-testid="wizard-run"
              className="inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80 px-5 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
              <Zap className="w-4 h-4" /> {running ? "Investigating…" : "Run investigation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
